'use client';
import React, { useState } from 'react';
import { Calendar, TrendingUp, TrendingDown, Users, Award, DollarSign, Activity } from 'lucide-react';
import { GSV_TREND_DATA, COMMISSION_HISTORY, RANK_DISTRIBUTION, DISTRIBUTORS } from '@/data/mockData';
import GSVTrendChart from './GSVTrendChart';
import ActiveRateTrendChart from './ActiveRateTrendChart';
import RankDistributionChart from './RankDistributionChart';
import CommissionTrendChart from './CommissionTrendChart';
import TopPerformersTable from './TopPerformersTable';

const PERIODS = ['Q1 2026', 'Q2 2025', 'Q3 2025', 'Q4 2025', 'Full Year 2025'];

export default function NetworkAnalyticsClient() {
  const [period, setPeriod] = useState('Q1 2026');

  const allDists = Object.values(DISTRIBUTORS);
  const activeCount = allDists?.filter(d => d?.active)?.length;
  const activeRate = Math.round((activeCount / allDists?.length) * 100);
  const totalGSV = allDists?.reduce((s, d) => s + d?.gsv, 0);
  const totalComm = allDists?.reduce((s, d) => s + d?.commissionThisMonth, 0);
  const execCount = allDists?.filter(d => ['Executive', 'Emerald', 'Diamond', 'Blue Diamond', 'Presidential Director']?.includes(d?.rank))?.length;
  const pexecCount = allDists?.filter(d => d?.rank === 'Provisional Executive')?.length;

  const prevGSV = 69400;
  const gsvChange = ((totalGSV - prevGSV) / prevGSV * 100)?.toFixed(1);
  const prevActiveRate = 85;
  const activeRateChange = activeRate - prevActiveRate;

  return (
    <div className="flex flex-col h-full bg-zinc-950">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-zinc-800 bg-zinc-900/40">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-zinc-100">Network Performance Analytics</h1>
            <p className="text-xs text-zinc-500 mt-0.5">GSV trends, active rate analysis, rank distribution, and top performer insights</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5">
              <Calendar size={14} className="text-zinc-500" />
              <select
                value={period}
                onChange={e => setPeriod(e?.target?.value)}
                className="bg-transparent text-sm text-zinc-300 outline-none cursor-pointer"
              >
                {PERIODS?.map(p => <option key={`np-${p}`} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto scrollbar-thin">
        <div className="max-w-screen-2xl mx-auto px-6 py-5 space-y-5">
          {/* KPI Row */}
          <div className="grid grid-cols-6 gap-3">
            {[
              { label: 'Total Network GSV', value: `${(totalGSV / 1000)?.toFixed(1)}K`, trend: `${gsvChange}%`, trendUp: parseFloat(gsvChange) >= 0, icon: TrendingUp, color: 'text-violet-400' },
              { label: 'Active Rate', value: `${activeRate}%`, trend: `${activeRateChange >= 0 ? '+' : ''}${activeRateChange}pp`, trendUp: activeRateChange >= 0, icon: Activity, color: 'text-emerald-400' },
              { label: 'Active Distributors', value: `${activeCount}`, trend: `of ${allDists?.length}`, trendUp: true, icon: Users, color: 'text-blue-400' },
              { label: 'Executive Count', value: `${execCount}`, trend: `+1 this quarter`, trendUp: true, icon: Award, color: 'text-amber-400' },
              { label: 'PEXEC Count', value: `${pexecCount}`, trend: 'At-risk', trendUp: false, icon: TrendingDown, color: 'text-orange-400' },
              { label: 'Total Commission', value: `$${(totalComm / 1000)?.toFixed(1)}K`, trend: '-1.3% vs Mar', trendUp: false, icon: DollarSign, color: 'text-cyan-400' },
            ]?.map(kpi => (
              <div key={`nakpi-${kpi?.label}`} className="bg-zinc-900 border border-zinc-800 rounded-xl px-3 py-3">
                <div className="flex items-center gap-2 mb-2">
                  <kpi.icon size={14} className={kpi?.color} />
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-600 truncate">{kpi?.label}</p>
                </div>
                <p className="text-xl font-bold font-mono tabular-nums text-zinc-100">{kpi?.value}</p>
                <p className={`text-[10px] font-semibold mt-0.5 ${kpi?.trendUp ? 'text-emerald-400' : 'text-red-400'}`}>{kpi?.trend}</p>
              </div>
            ))}
          </div>

          {/* Charts Row 1 */}
          <div className="grid grid-cols-3 gap-5">
            <div className="col-span-2">
              <GSVTrendChart data={GSV_TREND_DATA} />
            </div>
            <ActiveRateTrendChart data={GSV_TREND_DATA} />
          </div>

          {/* Charts Row 2 */}
          <div className="grid grid-cols-3 gap-5">
            <RankDistributionChart data={RANK_DISTRIBUTION} />
            <div className="col-span-2">
              <CommissionTrendChart data={COMMISSION_HISTORY} />
            </div>
          </div>

          {/* Top Performers */}
          <TopPerformersTable distributors={allDists} />
        </div>
      </div>
    </div>
  );
}